<template>
  <div class="min-h-screen bg-bochas-bg px-4 py-6 sm:py-8">
    <div class="mx-auto max-w-3xl">
      <div class="mb-6 sm:mb-8">
        <h1 class="text-2xl font-bold text-white sm:text-3xl">
          Importar / Exportar datos
        </h1>
        <p class="mt-2 text-sm text-bochas-text-muted">
          Descargá o subí datos por categoría.
        </p>
      </div>

      <form class="card-fbochas space-y-6 p-5 sm:p-6" @submit.prevent="onSubmit">
        <div>
          <label class="mb-2 block text-sm font-semibold text-white">
            Categoría
          </label>
          <SelectField
            v-model="category"
            :options="categoryOptions"
            :disabled="loading || exporting"
            placeholder="Seleccionar categoría"
          />
        </div>

        <div class="rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <div class="mb-4">
            <h2 class="text-base font-bold text-white">Exportar</h2>
            <p class="mt-1 text-sm text-bochas-text-muted">
              Descargá un JSON de la categoría seleccionada.
            </p>
          </div>
          <button
            type="button"
            class="rounded-lg border border-white/10 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="loading || exporting || !category"
            @click="onExport"
          >
            {{ exporting ? "Exportando..." : "Exportar JSON" }}
          </button>
        </div>

        <div class="rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <div class="mb-4">
            <h2 class="text-base font-bold text-white">Importar</h2>
            <p class="mt-1 text-sm text-bochas-text-muted">
              Subí un archivo CSV o JSON para la categoría seleccionada.
            </p>
            <p class="mt-2 text-xs text-bochas-text-muted">
              Antes de importar se descargará un respaldo de los datos actuales.
            </p>
          </div>

          <label class="mb-2 block text-sm font-semibold text-white">
            Archivo
          </label>
          <input
            type="file"
            :disabled="loading || exporting"
            accept=".csv,.json,application/json,text/csv"
            class="block w-full rounded-lg border border-white/10 bg-white/5 px-3 py-3 text-sm text-bochas-text-muted file:mr-4 file:rounded-md file:border-0 file:bg-bochas-rojo file:px-3 file:py-2 file:text-sm file:font-semibold file:text-white"
            @change="onFileChange"
          />
          <p class="mt-2 text-xs text-bochas-text-muted">
            Formatos permitidos: .csv y .json.
          </p>
        </div>

        <div
          v-if="error"
          class="rounded-lg border border-bochas-rojo bg-bochas-rojo/20 p-4 text-sm text-red-200"
        >
          {{ error }}
        </div>

        <div
          v-if="success"
          class="rounded-lg border border-green-400/30 bg-green-500/10 p-4 text-sm text-green-200"
        >
          {{ success }}
        </div>
        <a v-if="backupDownload" :href="backupDownload.url" :download="backupDownload.name"
          class="inline-block text-sm text-sky-200 underline underline-offset-4">
          Descargar nuevamente el respaldo previo
        </a>

        <div class="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <router-link
            to="/admin"
            class="rounded-lg border border-white/10 px-4 py-3 text-center text-sm font-semibold text-white transition-colors hover:bg-white/5"
          >
            Cancelar
          </router-link>
          <button
            type="submit"
            class="btn-bochas-rojo px-4 py-3 text-sm disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="loading || exporting"
          >
            {{ loading ? importPhase : "Importar archivo" }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onBeforeUnmount } from "vue";
import { useRoute } from "vue-router";
import { exportarDatos, importarDatos } from "@/services/imports/importsService";
import { listarAsociacionesAdmin } from "@/services/asociaciones/asociacionesService";
import SelectField from "@/components/Common/SelectField.vue";

const categoryOptions = [
  { value: "news", label: "Noticias" },
  { value: "stories", label: "Historias" },
  { value: "appointments", label: "Calendario" },
  { value: "tournaments", label: "Torneos y resultados" },
  { value: "associations", label: "Asociaciones" },
  { value: "clubs", label: "Clubes" },
  { value: "provinces", label: "Provincias" },
  { value: "contacts", label: "Teléfonos de contacto" },
  { value: "young-players", label: "Jóvenes Bochófilos" },
  { value: "referents", label: "Jugadores Históricos" },
  { value: "site-media", label: "Portadas" },
  { value: "site-settings", label: "Configuración" },
  { value: "federation", label: "La Federación" },
  { value: "accommodation", label: "Alojamiento" },
  { value: "categories", label: "Categorías" },
].sort((a, b) => a.label.localeCompare(b.label, "es", { sensitivity: "base" }));

const route = useRoute();
const initialCategory = categoryOptions.some((option) => option.value === route.query.category)
  ? route.query.category
  : "";
const category = ref(initialCategory);
const file = ref(null);
const loading = ref(false);
const exporting = ref(false);
const error = ref("");
const success = ref("");
const importPhase = ref("Importando...");
const backupDownload = ref(null);

onBeforeUnmount(() => {
  if (backupDownload.value) URL.revokeObjectURL(backupDownload.value.url);
});

function jsonDownload(data, name) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
  return { url, name };
}

async function backupBeforeImport(targetCategory) {
  let data;
  try {
    data = await exportarDatos(targetCategory);
  } catch {
    throw new Error("No se pudo preparar el respaldo. La importación no se inició.");
  }
  const createdAt = new Date().toISOString();
  const categoryName = categoryOptions.find((option) => option.value === targetCategory)?.label;
  const download = jsonDownload(
    { ...data, backup: { category: targetCategory, createdAt } },
    `Respaldo - ${categoryName} - ${createdAt.replace(/[:.]/g, "-")}.json`,
  );
  if (backupDownload.value) URL.revokeObjectURL(backupDownload.value.url);
  backupDownload.value = download;
}

function onFileChange(event) {
  const selected = event.target.files?.[0] || null;
  file.value = selected;
  error.value = "";
  success.value = "";
}

function isValidFile(selected) {
  return /\.(csv|json)$/i.test(selected.name);
}

function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\b(asociacion|federacion|bochas|provincia|cordoba|ciudad|civil|gral|general|san|de|del|la|el)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function slugify(value) {
  return normalizeText(value)
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

const associationAliases = {
  "asociacion-cordobesa-de-bochas-gral-san-martin": ["cordobesa", "cordobesa martin"],
  "asociacion-de-bochas-ciudad-de-san-francisco": ["francisco"],
  "asociacion-de-bochas-de-la-ciudad-de-morteros": ["morteros"],
  "asociacion-de-bochas-de-rio-tercero": ["rio tercero"],
  "asociacion-de-bochas-del-valle-de-traslasierras": ["traslasierras", "traslasierra", "valle traslasierras", "valle traslasierra"],
  "asociacion-de-bochas-portena": ["portena"],
  "asociacion-independencia-de-bochas": ["independencia"],
  "asociacion-rio-cuarto-de-bochas": ["rio cuarto"],
  "asociacion-villa-maria-de-bochas": ["villa maria"],
  "bochas-de-arroyito-asociacion-civil": ["arroyito"],
};

function readJsonFile(selected) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        resolve(JSON.parse(reader.result));
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsText(selected, "utf-8");
  });
}

async function prepareClubesFile(selected) {
  if (!selected.name.toLowerCase().endsWith(".json")) return selected;

  const parsed = await readJsonFile(selected);
  const clubsByAssociation = Array.isArray(parsed?.associations)
    ? parsed.associations.flatMap((association) =>
        (association.clubes || []).map((club) => ({
          ...club,
          asociacion: association.name,
          asociacionSlug: association.slug,
          provincia: club.provincia ?? association.provincia ?? association.provinceName,
          provinceId: club.provinceId ?? association.provinceId,
        })),
      )
    : Array.isArray(parsed)
    ? parsed
    : Array.isArray(parsed?.items)
    ? parsed.items
    : [];

  if (!clubsByAssociation.length) return selected;

  const data = await listarAsociacionesAdmin();
  const asociaciones = data.items || [];
  const byKey = new Map();
  asociaciones.forEach((item) => {
    [item.name, item.slug].filter(Boolean).forEach((value) => {
      const key = normalizeText(value);
      const matches = byKey.get(key) || [];
      if (!matches.includes(item.id)) matches.push(item.id);
      byKey.set(key, matches);
    });
  });
  const rows = [];
  const missing = new Set();
  const usedSlugs = new Map();

  function uniqueSlug(baseSlug, associationSlug) {
    const slug = baseSlug || associationSlug || "";
    const count = usedSlugs.get(slug) || 0;
    usedSlugs.set(slug, count + 1);
    if (count === 0) return slug;
    return `${slug}-${associationSlug || count + 1}`;
  }

  function findAsociacionId(name, slug) {
    const keys = [slug, name].filter(Boolean).map(normalizeText);
    for (const key of keys) {
      const exact = byKey.get(key);
      if (exact?.length > 1) throw new Error(`Hay varias asociaciones con el nombre ${name || slug}. Usá su slug o uuid.`);
      if (exact?.length === 1) return exact[0];
    }

    const aliases = associationAliases[slug] || [];
    for (const alias of aliases.map(normalizeText)) {
      const matches = asociaciones.filter((item) => {
        const itemKeys = [item.name, item.slug].filter(Boolean).map(normalizeText);
        return itemKeys.some(
          (itemKey) => itemKey === alias || itemKey.includes(alias) || alias.includes(itemKey),
        );
      });
      if (matches.length > 1) throw new Error(`La asociación ${name || slug} es ambigua.`);
      if (matches.length === 1) return matches[0].id;
    }

    const [key] = keys.sort((a, b) => a.length - b.length);
    if (!key || key.length < 5) return null;
    const matches = asociaciones.filter((item) => {
      const itemKeys = [item.name, item.slug].filter(Boolean).map(normalizeText);
      return itemKeys.some((itemKey) => itemKey.includes(key) || key.includes(itemKey));
    });
    if (matches.length > 1) throw new Error(`La asociación ${name || slug} es ambigua.`);
    return matches[0]?.id || null;
  }

  clubsByAssociation.forEach((club) => {
    const association = club.asociacion || club.association || null;
    const associationName =
      typeof association === "object" ? association?.name || "" : association || "";
    const associationSlug =
      club.asociacionSlug ||
      club.associationSlug ||
      (typeof association === "object" ? association?.slug || "" : "");
    const associationUuid = club.asociacionUuid || club.associationUuid ||
      (typeof association === "object" ? association?.uuid : "");
    const asociacionId = asociaciones.find((item) => associationUuid && item.uuid === associationUuid)?.id ||
      findAsociacionId(associationName, associationSlug);

    if (!asociacionId && (associationName || associationSlug || associationUuid || club.asociacionId || club.federacionId)) {
      missing.add(
        associationName ||
          associationSlug ||
          `${club.name || "Club sin nombre"} (exportá nuevamente con nombre o slug de asociación)`,
      );
      return;
    }

    const resolvedAssociation = asociaciones.find((item) => item.id === asociacionId);
    rows.push({
      ...club,
      name: club.name,
      slug: uniqueSlug(
        club.slug || slugify([club.name, club.locality].filter(Boolean).join(" ")),
        associationSlug,
      ),
      asociacionId,
      asociacion: resolvedAssociation?.name || "",
      asociacionSlug: resolvedAssociation?.slug || "",
      asociacionUuid: resolvedAssociation?.uuid || "",
      locality: club.locality || null,
      status: club.status || "published",
    });
  });

  if (missing.size) {
    throw new Error(`Faltan asociaciones cargadas: ${Array.from(missing).join(", ")}.`);
  }

  return new File([JSON.stringify(rows, null, 2)], selected.name, {
    type: "application/json",
  });
}

async function onSubmit() {
  error.value = "";
  success.value = "";

  if (!category.value) {
    error.value = "Selecciona una categoría.";
    return;
  }
  if (!file.value) {
    error.value = "Selecciona un archivo.";
    return;
  }
  if (!isValidFile(file.value)) {
    error.value = "El archivo debe ser CSV o JSON.";
    return;
  }

  loading.value = true;
  const targetCategory = category.value;
  const sourceFile = file.value;
  try {
    const selectedFile =
      targetCategory === "clubs" ? await prepareClubesFile(sourceFile) : sourceFile;
    importPhase.value = "Preparando respaldo...";
    await backupBeforeImport(targetCategory);
    importPhase.value = "Importando...";
    const data = await importarDatos(targetCategory, selectedFile);
    const count = data?.created ?? data?.imported ?? data?.count;
    const updated = data?.updated;
    success.value =
      count !== undefined
        ? `Importación completada. Registros creados: ${count}.${updated ? ` Registros actualizados: ${updated}.` : ""}`
        : "Importación completada.";
    if (data?.failed) {
      error.value = `${data.failed} registros no se pudieron importar. ${
        (data.errors || []).map((item) => `Fila ${item.row}: ${item.message}`).join(" ")
      }`;
    }
    file.value = null;
  } catch (err) {
    error.value =
      err.response?.data?.message || err.message || "No se pudo importar.";
  } finally {
    loading.value = false;
    importPhase.value = "Importando...";
  }
}

async function onExport() {
  error.value = "";
  success.value = "";

  if (!category.value) {
    error.value = "Selecciona una categoría.";
    return;
  }

  exporting.value = true;
  try {
    const data = await exportarDatos(category.value);
    const categoryName = categoryOptions.find((option) => option.value === category.value)?.label;
    const download = jsonDownload(data, `Exportación - ${categoryName}.json`);
    URL.revokeObjectURL(download.url);
    success.value = "Exportacion completada.";
  } catch (err) {
    error.value =
      err.response?.data?.message || err.message || "No se pudo exportar.";
  } finally {
    exporting.value = false;
  }
}
</script>
